import { IResourcePermissionWithResName } from "@/components/resources/tabs/access-restrictions/RestrictionsTab";
import { api } from "@/lib/api/api";
import { TimeZone } from "@/lib/timezones";
import { EntityType } from "@/lib/types/EntityType";
import {
  IResource,
  ISchedule,
  ISlot,
  Office,
  ResGetResource,
  ResourceType,
  ResponseWithStatus,
  UserRoles,
  parseDateOnlyString,
  parseTimeOnly
} from "@gettactic/api";
import { imageUrl } from "@gettactic/components/src/@v2/lib/utils";
import { type ClassValue, clsx } from "clsx";
import {
  addMinutes,
  differenceInHours,
  format,
  isSameDay,
  isToday,
  isTomorrow,
  isWeekend,
  isYesterday,
  millisecondsToHours,
  parseISO
} from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { twMerge } from "tailwind-merge";

export { imageUrl };

import {
  parseApiDate,
  parseApiDatetime,
  formatStartEndSlotHours,
  formatStartEndHours,
  dateParseISO
} from "@gettactic/helpers/src/date-time";
import { capitalize, getDefaultsHoursFromOffice } from "@gettactic/helpers";
export { groupBy } from "@gettactic/helpers/src/objects/groupBy";
export {
  parseApiDate,
  parseApiDatetime,
  dateParseISO,
  formatStartEndHours,
  formatStartEndSlotHours
};

export function convertTZ(date: Date, tzString = "UTC") {
  return utcToZonedTime(date, tzString);
}

export function getMinDateAllowedToReserve(
  officeTz: string,
  dates: Date[] = []
) {
  const d = convertTZ(new Date(), officeTz);
  d.setHours(0, 0, 0, 0);
  return [...dates, d].reduce((a, b) => (a < b ? a : b));
}

/**
 * Important Note: Be careful when modifying this function. It's used for a lot of "availability" display logic.
 * It's extra confusing since the API is very literal about maximum amount of time in the future a user can reserve, to the hour
 * but administrators think in terms of number of days in the future. We use logic in components elsewhere to compensate.
 */
export function getMaxDateAllowedToReserve(
  officeTz: string,
  office: Office | undefined,
  resType: ResourceType
) {
  if (!office || !officeTz) {
    return null;
  }
  const maxMin =
    resType === "meeting_room"
      ? office.meeting_room_reservation_period_min
      : office.workspace_reservation_period_min;
  if (maxMin === null) {
    return null;
  }
  const d = convertTZ(new Date(), officeTz);
  return addMinutes(d, maxMin);
}

export function isAllowedToReserve(
  day: Date,
  officeTimezone: string,
  office: Office | undefined,
  resType: ResourceType,
  roles: UserRoles[] = []
) {
  const max = getMaxDateAllowedToReserve(officeTimezone, office, resType);
  const min = getMinDateAllowedToReserve(officeTimezone);
  if (!roles.includes("owner") && day.getTime() < min.getTime()) {
    return false;
  }
  if (max && day.getTime() > max.getTime()) {
    return false;
  }
  return true;
}

export function toEntity(type: ResourceType): EntityType {
  switch (type) {
    case "desk":
    case "workspace":
      return "workspace";
    case "meeting_room":
      return "meeting_room";
    case "parking_lot":
    case "parking_space":
      return "parking";
    case "area":
      return "area";
    default:
      // biome-ignore lint/correctness/noSwitchDeclarations: <explanation>
      const exhaustiveCheck: string = type;
      throw new Error(`Unhandled resource type: ${exhaustiveCheck}`);
  }
}

/**
 * Return offset in hour between user's timezone
 * and param timezone
 *
 * @param timezone
 */
export function timezoneOffset(timezone: string) {
  const now = new Date();
  now.setHours(now.getHours(), 0, 0, 0);
  const nowTz = convertTZ(now, timezone);
  return millisecondsToHours(nowTz.getTime() - now.getTime());
}

/**
 * Returns true if timezone has a mistmatch with
 * user's timezone
 * @param timezone
 */
export function hasTimezoneMismatch(timezone: string) {
  return timezoneOffset(timezone) !== 0;
}

export function calculateHoursDifference(open: string, close: string) {
  const dateFromOpenString = parseTimeOnly(open);
  const dateFromCloseString = parseTimeOnly(close);
  return differenceInHours(dateFromCloseString, dateFromOpenString, {
    roundingMethod: "ceil"
  });
}

export function getTimeForReservation(
  start: Date,
  time: Date | null | undefined,
  office: Office | null
) {
  let validTime = time;
  if (!validTime) {
    const { startHour, startMinutes } = getDefaultsHoursFromOffice(office);
    validTime = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
      startHour,
      startMinutes,
      0
    );
  } else if (!isSameDay(start, validTime)) {
    validTime = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
      validTime.getHours(),
      validTime.getMinutes(),
      validTime.getSeconds()
    );
  }
  return validTime;
}

export function getPreviousMonday(from: Date = new Date()) {
  from.setDate(from.getDate() - ((from.getDay() + 6) % 7));
  return from;
}

/**
 * Returns true or false when passed a date string & office time zone
 * M - F = False, Saturday & Sunday = True.
 *
 */
export function hasWeekendDay(weekDatesISO: string[]): boolean {
  return !!weekDatesISO.find((x) => {
    const localDate = parseISO(x);
    return isWeekend(localDate);
  });
}

export function round(num: number, decimalPlaces = 2) {
  // biome-ignore lint/style/useExponentiationOperator: <explanation>
  const pow = Math.pow(10, decimalPlaces);
  return Math.round((num + Number.EPSILON) * pow) / pow;
}

export interface SelectOptionBase {
  label: string;
  value: string;
}

export function generateTimeZoneSelectOptions(timezones: TimeZone[]) {
  return timezones.map((timezone) => {
    return {
      label: timezone.label,
      value: timezone.id
    };
  });
}

export function findTimeZoneFromId(
  timezoneId: string,
  timezones: TimeZone[],
  short = false
): TimeZone | undefined {
  const timezone = timezones.find((timezone) => timezone.id === timezoneId);

  if (!timezone || !short) {
    return timezone;
  }

  // Use the readableOffset field for short format
  return {
    ...timezone,
    label: timezone.readableOffset
  };
}

export function findTimeZoneSelectOption<T extends SelectOptionBase>(
  timezone: TimeZone,
  options: T[]
) {
  return options.find((option) => option.value === timezone.id);
}

export function convertUTCToLocalDate(date: Date | string) {
  const cDate = new Date(date);
  return new Date(
    cDate.getUTCFullYear(),
    cDate.getUTCMonth(),
    cDate.getUTCDate(),
    cDate.getUTCHours(),
    cDate.getUTCMinutes(),
    cDate.getUTCSeconds()
  );
}

export function areAll<T>(col: T[], what?: T): boolean {
  const is = col[0];
  if (col.findIndex((x) => x !== is) > -1) {
    return false;
  }
  return typeof what !== "undefined" ? is === what : true;
}

export function replaceVarsBulkName(template: string, index: number): string {
  const now = new Date();
  const indexMatch = template.match(/({{number:(\d+)}})/);
  const currentDate = format(now, "yyyy-MM-dd");
  const currentTime = format(now, "HH:mm:ss");
  let newName: string;
  if (indexMatch?.[1] && indexMatch[2]) {
    newName = template.replaceAll(
      indexMatch[1],
      (index + Number.parseInt(indexMatch[2], 10)).toString()
    );
  } else {
    newName = template.replaceAll("{{number}}", (index + 1).toString());
  }
  newName = newName.replaceAll("{{date}}", currentDate);
  newName = newName.replaceAll("{{time}}", currentTime);
  return newName;
}

export const getDaySplit = (
  day: string
): { year: string; month: string; day: string } => {
  const daySplit = day.split("-");
  const year = daySplit[0];
  const month = daySplit[1];
  const dayValue = daySplit[2];

  return { year: year, month: month, day: dayValue };
};

export const mobileParseApiDate = (day: string): Date => {
  const daySplit = getDaySplit(day);

  const timeString = day.split("T")[1].slice(0, -1);
  const timeSplit = timeString.split(":");
  const hours = Number.parseInt(timeSplit[0]);
  const minutes = Number.parseInt(timeSplit[1]);
  const seconds = Number.parseInt(timeSplit[2]);

  return new Date(
    Number.parseInt(daySplit.year),
    Number.parseInt(daySplit.month) - 1,
    Number.parseInt(daySplit.day),
    hours,
    minutes,
    seconds
  );
};

/**
 * Creates a human friendly date string in relation to the current date. For example, if the date is today, it will return "Today".
 */
export const getDistanceToNow = (_date: string) => {
  const date = mobileParseApiDate(_date);

  // Requires explicit checks since date-fns will return "in 20 minutes", "12 hours ago", etc.
  if (isToday(date)) {
    return "Today";
  }
  if (isTomorrow(date)) {
    return "Tomorrow";
  }
  if (isYesterday(date)) {
    return "Yesterday";
  }

  // Outside of the above exceptions, show date formatted as "Wednesday, Sep. 01"
  return format(date, "eeee, MMM. dd");
};

export function primaryScheduleResource(slots: ISlot[]): IResource {
  // Looks for resources by priority in the order of the array
  const resourceTypeOrder: ResourceType[] = [
    "desk",
    "workspace",
    "meeting_room",
    "parking_space",
    "parking_lot"
  ];

  for (const resourceType of resourceTypeOrder) {
    const slot = slots.find((slot) => slot.resource.type === resourceType);
    if (slot) {
      return slot.resource;
    }
  }

  return slots[0].resource;
}

/**
 * More centralized location to store resource images and styles so that they can be easily updated in the future.
 */
export function scheduleResourceStyling(
  resourceType: ResourceType,
  customParkingVerbiage = false
) {
  let resType = resourceType;
  if (customParkingVerbiage) {
    if (resourceType === "parking_lot" || resourceType === "parking_space") {
      resType = "locker";
    }
  }
  const resourceImages: Partial<
    Record<
      ResourceType,
      {
        title: string;
        image: string;
        alt: string;
        style: string;
      }
    >
  > = {
    desk: {
      title: "Desk",
      image: "https://cdn.gettactic.com/website/images/desk.webp",
      alt: "Desk with monitor and small plant",
      style: "bg-primary-lighter"
    },
    workspace: {
      title: "Desk",
      image: "https://cdn.gettactic.com/website/images/desk.webp",
      alt: "Desk with monitor and small plant",
      style: "bg-primary-lighter"
    },
    meeting_room: {
      title: "Room",
      image: "https://cdn.gettactic.com/website/images/meeting_room.webp",
      alt: "Meeting room with projector screen and chairs",
      style: "bg-primary-lighter"
    },
    parking_lot: {
      title: "Parking",
      image: "https://cdn.gettactic.com/website/images/parking.webp",
      alt: "Car parked in a commercial garage",
      style: "bg-gray-100"
    },
    parking_space: {
      title: "Parking",
      image: "https://cdn.gettactic.com/website/images/parking.webp",
      alt: "Car parked in a commercial garage",
      style: "bg-gray-100"
    },
    locker: {
      title: "Locker",
      image: "https://cdn.gettactic.com/website/images/locker.png",
      alt: "Locker",
      style: "bg-gray-100"
    }
  };
  return resourceImages[resType] ?? resourceImages.desk;
}

/**
 * Gets the resource chain for the resourceId provided i.e. the parent_id chain
 *
 * TODO: Mobile code that was borrowed since we don't have office_name, floor_name, etc. on the elements
 * of the scheduleMeData query. These resourceChain functions should be removed once we have the data.
 */
export const getResourceChain = async (parentId: string | null = null) => {
  const chain = [];
  let nextId: string | null = parentId;
  while (nextId !== null) {
    const result: ResponseWithStatus<{ resource: ResGetResource }> =
      await api.client.organizations.resourceGet(nextId);
    if (!result || !result.result || !result.result.resource) {
      return undefined;
    }
    chain.push(result.result.resource);
    nextId = result.result.resource.parent_id;
  }
  return chain;
};

export function isWorkingLocationOffice(location: string | null) {
  return location?.startsWith("offi_");
}

export function getResourceTypeName(
  resource: IResource,
  parkingVerbiage: string | null
) {
  if (resource.type === "desk") {
    return "Workspace";
  }
  if (resource.type === "workspace") {
    return "Workspace";
  }
  if (resource.type === "meeting_room") {
    return "Room";
  }
  if (resource.type === "parking_lot") {
    return parkingVerbiage ? capitalize(`${parkingVerbiage} group`) : "Parking";
  }
  if (resource.type === "parking_space") {
    return parkingVerbiage ? capitalize(parkingVerbiage) : "Parking Space";
  }
  return "Desk";
}

export function sortPermissionsArray(
  permissions: IResourcePermissionWithResName[]
) {
  return permissions.sort((a, b) => {
    if (a.since && b.since) {
      return new Date(a.since) > new Date(b.since) ? 1 : -1;
    }
    if (a.since) {
      return -1;
    }
    if (b.since) {
      return 1;
    }
    if (a.until && b.until) {
      return new Date(a.until) > new Date(b.until) ? 1 : -1;
    }
    if (a.until) {
      return -1;
    }
    if (b.until) {
      return 1;
    }
    return a.id > b.id ? 1 : -1;
  });
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getNewUrl(organizationSlug: string, actualSlug?: string) {
  const currentHostName = window.location.hostname;
  const newHostName = currentHostName.replace(
    actualSlug ?? "",
    organizationSlug
  );
  // this way we keep same protocol, domain, path, params, etc
  const newUrl = window.location.href.replace(currentHostName, newHostName);
  return newUrl;
}

export function formatDisplayName(name: string) {
  return name.trim().replace(/\.$/, "");
}
